import { useSearchParams } from "react-router";
import { Bot, CalendarDays, Coins, FileSignature, GitBranch, Mail, Sparkles, UserCheck, Workflow, SlidersHorizontal } from "lucide-react";
import { useTranslation } from "@ncp/i18n";
import { Badge, EmptyState, PageHeader, Skeleton, Tabs, TabsContent, TabsList, TabsTrigger } from "@ncp/ui";
import { ProposalList } from "@ncp/assistant-sdk";
import { useWorkflowProposals } from "../features/studio/api";
import { FeesTab, HolidaysTab, TemplatesTab } from "../features/studio/ConfigTabs";
import { AnalysisPolicyTab } from "../features/studio/AnalysisPolicyTab";
import { DecisionTemplatesEditor, SignatoriesEditor } from "../features/studio/DecisionControlsTabs";
import { WorkflowGallery } from "../features/builder/Gallery";
import { useHelpTopic } from "../app/console-context";

// The notification thresholds, the dominance threshold and the HHI bands are edited in the analysis policy (the one place they are defined,
// condition 2.14); the legacy "thresholds" tab is retired and an old link to it opens the analysis policy.
const tabs = ["definitions", "templates", "decisionTemplates", "signatories", "holidays", "fees", "analysis", "advisor"] as const;
type Tab = (typeof tabs)[number];

/** Workflow Studio: the gallery of workflows (each opens in the full-screen Workflow Builder), the Change Advisor, and the regulatory configuration tables. */
export function WorkflowStudioPage() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const requested = params.get("tab") === "thresholds" ? "analysis" : params.get("tab");
  const tab: Tab = tabs.includes(requested as Tab) ? (requested as Tab) : "definitions";
  const proposals = useWorkflowProposals();
  useHelpTopic({ title: t("admin:workflowStudio.title"), docsAnchor: "workflow-builder", body: (<><p>{t("admin:builder.help.what")}</p><p>{t("admin:builder.help.versions")}</p><p>{t("admin:builder.help.copilot")}</p></>) });

  const pendingAdvice = (proposals.data?.items ?? []).filter((p) => p.status === "Proposed").length;

  return (
    <div>
      <PageHeader title={t("admin:workflowStudio.title")} description={t("admin:workflowStudio.subtitle")} actions={pendingAdvice ? <Badge variant="ai" dot pulse><Bot /> {t("admin:workflowStudio.pendingAdvice", { count: pendingAdvice })}</Badge> : null} />
      <Tabs value={tab} onValueChange={(v) => setParams((p) => { p.set("tab", v); return p; }, { replace: true })}>
        <TabsList variant="underline" className="mb-4 flex-wrap">
          <TabsTrigger value="definitions" variant="underline"><Workflow /> {t("admin:workflowStudio.definitions")}</TabsTrigger>
          <TabsTrigger value="templates" variant="underline"><Mail /> {t("admin:workflowStudio.templates")}</TabsTrigger>
          <TabsTrigger value="decisionTemplates" variant="underline"><FileSignature /> {t("admin:decisionControls.tabTemplates")}</TabsTrigger>
          <TabsTrigger value="signatories" variant="underline"><UserCheck /> {t("admin:decisionControls.tabSignatories")}</TabsTrigger>
          <TabsTrigger value="holidays" variant="underline"><CalendarDays /> {t("admin:workflowStudio.holidays")}</TabsTrigger>
          <TabsTrigger value="fees" variant="underline"><Coins /> {t("admin:workflowStudio.fees")}</TabsTrigger>
          <TabsTrigger value="analysis" variant="underline"><SlidersHorizontal /> {t("admin:analysisPolicy.tab")}</TabsTrigger>
          <TabsTrigger value="advisor" variant="underline"><Sparkles /> {t("admin:workflowStudio.advisor")} {pendingAdvice ? <Badge size="sm" variant="gold">{pendingAdvice}</Badge> : null}</TabsTrigger>
        </TabsList>

        <TabsContent value="definitions"><WorkflowGallery /></TabsContent>
        <TabsContent value="templates"><TemplatesTab /></TabsContent>
        <TabsContent value="decisionTemplates"><DecisionTemplatesEditor /></TabsContent>
        <TabsContent value="signatories"><SignatoriesEditor /></TabsContent>
        <TabsContent value="holidays"><HolidaysTab /></TabsContent>
        <TabsContent value="fees"><FeesTab /></TabsContent>
        <TabsContent value="analysis"><AnalysisPolicyTab /></TabsContent>
        <TabsContent value="advisor">
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground"><GitBranch className="me-1 inline size-4 text-aegold-700" /> {t("admin:workflowStudio.advisorHint")}</p>
            {proposals.isLoading ? <Skeleton className="h-64" /> : (proposals.data?.items.length ?? 0) === 0 ? <EmptyState icon={Sparkles} title={t("admin:workflowStudio.noAdvice")} description={t("admin:workflowStudio.noAdviceHint")} /> : <ProposalList proposals={proposals.data!.items} invalidate={[["proposals"], ["workflow-definitions"], ["workflow-review"]]} defaultOpenSingle />}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
